import React from 'react';
import DocumentTimeline from '../../../shared/DocumentTimeline';
import useDocumentMilestoneLog from '../../../shared/useDocumentMilestoneLog';

// Every milestone type recordable against a Quote (see quoteController.js's
// own recordMilestone calls) — a type missing here falls back to its raw
// key so a future milestone never renders blank.
const MILESTONE_LABELS = {
  draft: 'Quote created',
  copied_from: 'Copied from',
  copied_to: 'Copied to',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
  converted: 'Converted to Sales Order',
  notify_failed: 'Portal email not sent',
  payment_recorded: 'Payment recorded',
  payment_deleted: 'Payment deleted',
  payment_voided: 'Receipt voided',
  discount_added: 'Discount added',
  discount_changed: 'Discount changed',
  discount_removed: 'Discount removed',
};

// A handful of Quotes reached a step before document_milestones recorded
// it and were never backfilled — their own quotes.*_at column stands in.
const LEGACY_DATES = {
  draft: 'createdAt',
  sent: 'sentAt',
  accepted: 'acceptedAt',
  rejected: 'rejectedAt',
  withdrawn: 'withdrawnAt',
  expired: 'expiredAt',
};

// Right-hand sidebar on QuotePage.js — the Quote's Status History, the same
// one-entry-per-event shape as SalesOrderTimeline.js: every recorded
// milestone, oldest first, each with who did it and any comment (a
// Withdraw/Reject note, an Edited change). Copied to/from link to the other
// Quote; Converted links to the Sales Order. The page header already shows
// the live status pill, so nothing is "current" here.
function QuoteTimeline({ quoteMeta, documentId }) {
  const recorded = useDocumentMilestoneLog('quote', documentId);
  if (!quoteMeta) return null;

  const legacy = Object.entries(LEGACY_DATES)
    .filter(([milestone, field]) => quoteMeta[field] && !recorded.some((r) => r.milestone === milestone))
    .map(([milestone, field]) => ({ milestone, occurred_at: quoteMeta[field] }));
  const at = (r) => (r.occurred_at ? new Date(r.occurred_at).getTime() : 0);
  const rows = legacy.length ? [...recorded, ...legacy].sort((a, b) => at(a) - at(b)) : recorded;
  const converted = quoteMeta.convertedSalesOrder;

  const steps = rows.map((r, idx) => {
    let link = r.related ? { to: r.related.link, label: r.related.display_number } : null;
    if (r.milestone === 'converted' && converted) link = { to: `/sales-orders/${converted.id}`, label: converted.display_number };
    return {
      key: `${r.milestone}-${idx}`,
      label: MILESTONE_LABELS[r.milestone] || r.milestone,
      reached: true,
      isCurrent: false,
      date: r.occurred_at,
      link,
      by: r.user_name || null,
      comment: r.comment || null,
    };
  });
  return <DocumentTimeline steps={steps} title="Status History" />;
}

export default QuoteTimeline;
